import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useAttackPackageTemplates,
  useCreateAttackPackageTemplate,
  useDeleteAttackPackageTemplate,
  useUpdateAttackPackageTemplate,
  type AttackPackageTemplateInput,
} from '../api/queries';
import {
  ATTACK_PACKAGE_CATEGORY_LABELS,
  buildingLabel,
  type AttackPackageCategory,
  type AttackPackageTemplate,
  type UnitCounts,
} from '../api/types';
import { useToast } from '../components/Toast';
import { useWorldOptions } from '../api/queries';
import type { User } from '../api/types';

function unitsToText(units: UnitCounts | null): string {
  if (!units) return '';
  return Object.entries(units)
    .map(([unit, count]) => `${unit}:${count}`)
    .join(', ');
}

const CATAPULT_TARGET_KEYS = [
  'main', 'barracks', 'stable', 'garage', 'watchtower', 'snob', 'smith',
  'place', 'statue', 'market', 'wood', 'stone', 'iron', 'farm', 'storage',
  'hide', 'wall', 'church_f',
];

function emptyForm(): AttackPackageTemplateInput {
  return {
    name: '',
    category: 'attack',
    minUnitsText: '',
    exportUnitsText: '',
    catapultTarget: null,
    dsWorkbenchType: null,
    priority: 5,
    preventSplitPerVillage: false,
  };
}

function formFromTemplate(template: AttackPackageTemplate): AttackPackageTemplateInput {
  return {
    name: template.name,
    category: template.category,
    minUnitsText: unitsToText(template.minUnits),
    exportUnitsText: unitsToText(template.exportUnits),
    catapultTarget: template.catapultTarget,
    dsWorkbenchType: template.dsWorkbenchType,
    priority: template.priority,
    preventSplitPerVillage: template.preventSplitPerVillage,
  };
}

/**
 * Pakettyp-Vorlagen des Angriffsplaners: Mindest-Truppenzusammensetzung
 * (für die Verfügbarkeitsprüfung) plus optionale abweichende
 * Export-Einheiten (nur kosmetisch für DS-Workbench, siehe Hinweistext).
 */
export function AttackPackageTemplatesPage({ user }: { user: User }) {
  // In der URL statt in reinem Komponenten-State -- ein Reload oder ein
  // Wechsel zu den Kriegsplänen und zurück soll die gewählte Welt nicht
  // verlieren (explizite Nutzeranfrage, gleiches Muster wie AdminUsersPage.tsx).
  const [searchParams, setSearchParams] = useSearchParams();
  const needsWorldChoice = user.world === null;
  const worldKey = searchParams.get('welt') ?? '';
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const templates = useAttackPackageTemplates(activeWorld || undefined);
  const create = useCreateAttackPackageTemplate(activeWorld || undefined);
  const update = useUpdateAttackPackageTemplate(activeWorld || undefined);
  const remove = useDeleteAttackPackageTemplate(activeWorld || undefined);
  const toast = useToast();

  const [form, setForm] = useState<AttackPackageTemplateInput>(emptyForm());
  const [editingId, setEditingId] = useState<number | null>(null);

  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const rows = [...(templates.data?.templates ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const startEdit = (template: AttackPackageTemplate) => {
    setEditingId(template.id);
    setForm(formFromTemplate(template));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (editingId !== null) {
      update.mutate(
        { id: editingId, ...form },
        {
          onSuccess: () => {
            toast.push('success', 'Pakettyp-Vorlage wurde gespeichert.');
            cancelEdit();
          },
          onError: (err) => toast.push('error', err.message),
        },
      );
    } else {
      create.mutate(form, {
        onSuccess: () => {
          toast.push('success', 'Pakettyp-Vorlage wurde erstellt.');
          setForm(emptyForm());
        },
        onError: (err) => toast.push('error', err.message),
      });
    }
  };

  const confirmDelete = (template: AttackPackageTemplate) => {
    if (!window.confirm(`Pakettyp-Vorlage "${template.name}" löschen?`)) return;
    remove.mutate(
      { id: template.id },
      {
        onSuccess: () => toast.push('success', 'Pakettyp-Vorlage wurde gelöscht.'),
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Pakettyp-Vorlagen</h1>
          <p className="muted">
            Mindest-Truppenzusammensetzung für den Angriffsplaner -- eine Vorlage beschreibt EIN Paket (z. B.
            "Wallbreaker"), das später Wellen in einem Kriegsplan zugewiesen wird.
          </p>
        </div>
        <Link to={`/angriffsplaner${worldKey ? `?welt=${worldKey}` : ''}`} className="btn btn-sm">
          Zu den Kriegsplänen
        </Link>
      </div>

      {!needsWorldChoice ? null : (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setSearchParams({ welt: event.target.value }, { replace: true })} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && <p className="muted center pad">Wähle eine Welt, um ihre Pakettyp-Vorlagen zu sehen.</p>}

      {activeWorld && (
        <>
          {error instanceof ApiError && <div className="flash flash-error">{error.message}</div>}

          <form className="form" onSubmit={handleSubmit} noValidate>
            <div className="field-row">
              <label className="field grow">
                <span>Name</span>
                <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </label>
              <label className="field">
                <span>Kategorie</span>
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as AttackPackageCategory }))}
                >
                  {Object.entries(ATTACK_PACKAGE_CATEGORY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Priorität (1 = zuerst)</span>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={form.priority}
                  onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) }))}
                />
              </label>
            </div>

            <label className="field">
              <span>Mindest-Einheiten (Format: einheit:anzahl, mit Komma oder Zeilenumbruch getrennt)</span>
              <textarea
                rows={2}
                value={form.minUnitsText}
                onChange={(e) => setForm((f) => ({ ...f, minUnitsText: e.target.value }))}
                placeholder="axe:6000, light:2500, ram:300"
                required
              />
            </label>

            <label className="field">
              <span>Export-Einheiten für DS-Workbench (optional)</span>
              <textarea
                rows={2}
                value={form.exportUnitsText}
                onChange={(e) => setForm((f) => ({ ...f, exportUnitsText: e.target.value }))}
                placeholder="axe:99999, light:99999, ram:99999"
              />
              <span className="hint">
                Nur für den DS-Workbench-Export -- ohne Einfluss auf die Verfügbarkeitsprüfung. Leer lassen, um dieselben
                Zahlen wie oben zu exportieren.
              </span>
            </label>

            <div className="field-row">
              {form.category === 'catapult' && (
                <label className="field">
                  <span>Katapult-Ziel</span>
                  <select
                    value={form.catapultTarget ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, catapultTarget: e.target.value || null }))}
                  >
                    <option value="">-- kein Ziel --</option>
                    {CATAPULT_TARGET_KEYS.map((value) => (
                      <option key={value} value={value}>
                        {buildingLabel(value)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="field">
                <span>DS-Workbench-Angriffstyp (optional)</span>
                <input
                  type="number"
                  value={form.dsWorkbenchType ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, dsWorkbenchType: e.target.value ? Number(e.target.value) : null }))}
                />
              </label>
            </div>

            {(form.category === 'attack' || form.category === 'wallbreaker') && (
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={form.preventSplitPerVillage}
                  onChange={(e) => setForm((f) => ({ ...f, preventSplitPerVillage: e.target.checked }))}
                />
                <span>
                  Kein Splitting -- ein Dorf bekommt nie mehr als EIN Paket vom Typ "Angriff"/"Wallbreaker" (über alle
                  Vorlagen dieser Typen hinweg, nicht nur diese hier)
                </span>
              </label>
            )}

            <div className="button-row">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Wird gespeichert …' : editingId !== null ? 'Speichern' : 'Erstellen'}
              </button>
              {editingId !== null && (
                <button type="button" className="btn btn-ghost" onClick={cancelEdit}>
                  Abbrechen
                </button>
              )}
            </div>
          </form>

          {templates.isPending && <p className="muted">Wird geladen …</p>}
          {!templates.isPending && rows.length === 0 && <p className="muted center pad">Noch keine Pakettyp-Vorlagen angelegt.</p>}

          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Kategorie</th>
                    <th>Mindest-Einheiten</th>
                    <th>Export-Einheiten</th>
                    <th>Priorität</th>
                    <th>Splitting</th>
                    <th className="right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((template) => (
                    <tr key={template.id}>
                      <td>{template.name}</td>
                      <td>{ATTACK_PACKAGE_CATEGORY_LABELS[template.category]}</td>
                      <td>{unitsToText(template.minUnits)}</td>
                      <td>{template.exportUnits ? unitsToText(template.exportUnits) : <span className="muted">wie Mindest-Einheiten</span>}</td>
                      <td>{template.priority}</td>
                      <td>{template.preventSplitPerVillage ? 'nicht erlaubt' : <span className="muted">erlaubt</span>}</td>
                      <td className="right">
                        <div className="actions">
                          <button type="button" className="btn btn-sm" onClick={() => startEdit(template)}>
                            Bearbeiten
                          </button>
                          <button type="button" className="btn btn-danger btn-sm" disabled={remove.isPending} onClick={() => confirmDelete(template)}>
                            Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
